import { Fragment, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from "recharts";
import { ChevronDown, ChevronRight } from "lucide-react";
import { fmtEUR, fmtEURshort, fmtPct, slug } from "@/lib/format";
import { PIE_COLORS } from "@/components/app/charts";

const AXIS = { fontSize: 12, fill: "#756B64" };
const Box = ({ title, subtitle, testId, children, className = "" }) => (
  <section className={`card-soft p-5 sm:p-6 ${className}`} data-testid={testId}>
    <h2 className="text-lg font-medium">{title}</h2>
    {subtitle && <p className="text-xs text-[#756B64] mt-1">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </section>
);
const EurTip = ({ active, payload, label }) => active && payload?.length ? (
  <div className="rounded-xl bg-white border border-[#EAE3DC] shadow-lg px-3 py-2 text-xs">
    <div className="font-semibold mb-1">{label || payload[0].payload.marca}</div>
    {payload.map((p) => <div key={p.dataKey} className="flex gap-3"><span style={{ color: p.color || p.payload.fill }}>{p.name}</span><span className="ml-auto tabular font-semibold">{fmtEUR(p.value)}</span></div>)}
  </div>
) : null;
const BubbleTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const m = payload[0].payload;
  return <div className="rounded-xl bg-white border border-[#EAE3DC] shadow-lg px-3 py-2 text-xs"><div className="font-semibold mb-1">{m.marca}</div><div>Pezzi: <b className="tabular">{m.quantita}</b></div><div>Margine: <b className="tabular">{fmtPct(m.margine_pct)}</b></div><div>Ricavo: <b className="tabular">{fmtEUR(m.ricavo)}</b></div></div>;
};

export function BrandCharts({ marche }) {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Box title="Margine per marca" subtitle="Margine lordo e netto (dopo omaggi e scarti)" testId="chart-margine-marche" className="lg:col-span-2">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={marche} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#EAE3DC" vertical={false} />
            <XAxis dataKey="marca" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtEURshort} tick={AXIS} axisLine={false} tickLine={false} width={60} />
            <Tooltip content={<EurTip />} cursor={{ fill: "#F4EFEA" }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="ricavo" name="Ricavo" fill="#E8C5C8" radius={[6, 6, 0, 0]} maxBarSize={28} />
            <Bar dataKey="margine" name="Margine lordo" fill="#8AA090" radius={[6, 6, 0, 0]} maxBarSize={28} />
            <Bar dataKey="margine_netto" name="Margine netto" fill="#8C6D62" radius={[6, 6, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Box title="Quota di fatturato per marca" subtitle="Sul ricavo prodotti del periodo" testId="chart-quota-marche">
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <div className="w-[200px] h-[200px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={marche} dataKey="ricavo" nameKey="marca" innerRadius={58} outerRadius={94} paddingAngle={2} stroke="none">{marche.map((m, i) => <Cell key={m.marca} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip content={<EurTip />} /></PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="flex-1 w-full space-y-2">
            {marche.map((m, i) => <li key={m.marca} className="flex items-center gap-2 text-sm"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /><span className="truncate">{m.marca}</span><span className="ml-auto tabular font-medium">{fmtPct(m.quota_pct)}</span></li>)}
          </ul>
        </div>
      </Box>
      <Box title="Margine % vs volumi" subtitle="Ogni bolla è una marca; la dimensione è il ricavo" testId="chart-bubble-marche">
        <ResponsiveContainer width="100%" height={220}>
          <ScatterChart margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#EAE3DC" />
            <XAxis type="number" dataKey="quantita" name="Pezzi" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis type="number" dataKey="margine_pct" name="Margine %" unit="%" tick={AXIS} axisLine={false} tickLine={false} width={48} domain={["auto", "auto"]} />
            <ZAxis type="number" dataKey="ricavo" range={[120, 1400]} />
            <Tooltip content={<BubbleTip />} />
            <Scatter data={marche}>{marche.map((m, i) => <Cell key={m.marca} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={0.75} />)}</Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
    </div>
  );
}

const COLS = ["Pezzi", "Ricavo", "Costo venduto", "Margine lordo", "%", "Omaggi", "Scarti", "Margine netto", "%", "Quota fatt."];
const MARCA_COLS = ["Spese brand", "Margine netto marca"];
function MarcaCells({ sb, mnm, bold }) {
  const td = "px-3 py-2.5 text-right tabular";
  return (
    <>
      <td className={`${td} text-[#A2513C]`}>{sb ? fmtEUR(sb) : "—"}</td>
      <td className={`${td} ${bold ? "font-semibold" : ""} ${mnm < 0 ? "text-[#B4553D]" : "text-[#4E6656]"}`}>{mnm == null ? "—" : fmtEUR(mnm)}</td>
    </>
  );
}

function Cells({ r, totRicavo, bold }) {
  const td = "px-3 py-2.5 text-right tabular";
  return (
    <>
      <td className={td}>{r.quantita}</td>
      <td className={td}>{fmtEUR(r.ricavo)}</td>
      <td className={td}>{fmtEUR(r.cogs)}</td>
      <td className={`${td} ${bold ? "font-semibold" : ""}`}>{fmtEUR(r.margine)}</td>
      <td className={`${td} text-[#756B64]`}>{fmtPct(r.margine_pct)}</td>
      <td className={td}>{r.omaggi_qty ? `${r.omaggi_qty} · ${fmtEUR(r.omaggi_valore)}` : "—"}</td>
      <td className={td}>{r.scarti_qty ? `${r.scarti_qty} · ${fmtEUR(r.scarti_valore)}` : "—"}</td>
      <td className={`${td} ${bold ? "font-semibold" : ""} text-[#4E6656]`}>{fmtEUR(r.margine_netto)}</td>
      <td className={`${td} text-[#756B64]`}>{fmtPct(r.margine_netto_pct)}</td>
      <td className={`${td} text-[#756B64]`}>{fmtPct(totRicavo ? (r.ricavo / totRicavo) * 100 : null)}</td>
    </>
  );
}

export function GroupTable({ rows, prodotti, totali, by, testId }) {
  const [open, setOpen] = useState({});
  const isMarca = by === "marca";
  const keyOf = (r) => (isMarca ? r.marca_id || r.marca : r.linea);
  const prodsOf = (r) => prodotti.filter((p) => (isMarca ? (p.marca_id || p.marca) === keyOf(r) : p.linea === r.linea));
  return (
    <div className="card-soft overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm min-w-[1150px]">
        <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
          <tr><th className="text-left font-semibold px-3 py-3">{isMarca ? "Marca" : "Tipologia"}</th>{[...COLS, ...(isMarca ? MARCA_COLS : [])].map((h, i) => <th key={i} className="text-right font-semibold px-3 py-3">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[#EAE3DC]">
          {rows.map((r, i) => {
            const k = keyOf(r), isOpen = open[k];
            return (
              <Fragment key={k}>
                <tr data-testid={`${testId}-row-${i}`} onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))} className="cursor-pointer hover:bg-[#FAF7F5]">
                  <td className="px-3 py-2.5 font-medium"><span className="inline-flex items-center gap-1.5">{isOpen ? <ChevronDown className="h-4 w-4 text-[#8C6D62]" /> : <ChevronRight className="h-4 w-4 text-[#8C6D62]" />}{isMarca ? r.marca : r.linea}{isMarca && <span className="text-[10px] text-[#756B64] font-normal">({r.n_prodotti} prod.)</span>}</span></td>
                  <Cells r={r} totRicavo={totali.ricavo} bold />
                  {isMarca && <MarcaCells sb={r.spese_brand} mnm={r.margine_netto_marca} bold />}
                </tr>
                {isOpen && prodsOf(r).map((p, j) => (
                  <tr key={p.prodotto_id} data-testid={`${testId}-row-${i}-prodotto-${j}`} className="bg-[#FAF7F5]/60 text-[13px]">
                    <td className="pl-10 pr-3 py-2">{p.nome}<div className="text-[10px] text-[#756B64]">{p.sku} · {isMarca ? p.linea : p.marca} · costo medio {fmtEUR(p.costo_medio)}</div></td>
                    <Cells r={p} totRicavo={totali.ricavo} />
                    {isMarca && <MarcaCells />}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-[#F4EFEA] font-semibold">
          <tr data-testid={`${testId}-totale`}><td className="px-3 py-3">Totale</td><Cells r={totali} totRicavo={totali.ricavo} bold />{isMarca && <MarcaCells sb={totali.spese_brand_marche} mnm={totali.margine_netto_marche} bold />}</tr>
        </tfoot>
      </table>
    </div>
  );
}

const heat = (pct) => (pct == null ? "transparent" : `rgba(138,160,144,${Math.min(0.55, Math.max(0.08, (pct - 30) / 90))})`);

export function MatriceTable({ matrice, marche, linee }) {
  const cell = Object.fromEntries(matrice.map((c) => [`${c.marca}|${c.linea}`, c]));
  const cols = linee.map((l) => l.linea);
  return (
    <div className="card-soft overflow-x-auto" data-testid="matrice-table">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
          <tr><th className="text-left font-semibold px-3 py-3">Marca \ Tipologia</th>{cols.map((c) => <th key={c} className="text-right font-semibold px-3 py-3">{c}</th>)}<th className="text-right font-semibold px-3 py-3">Totale marca</th></tr>
        </thead>
        <tbody className="divide-y divide-[#EAE3DC]">
          {marche.map((m, i) => (
            <tr key={m.marca} data-testid={`matrice-row-${i}`}>
              <td className="px-3 py-2.5 font-medium">{m.marca}</td>
              {cols.map((c) => { const x = cell[`${m.marca}|${c}`]; return (
                <td key={c} data-testid={`matrice-cell-${i}-${slug(c)}`} className="px-3 py-2.5 text-right tabular" style={{ background: heat(x?.margine_pct) }}>
                  {x ? <><div className="font-semibold">{fmtEUR(x.margine)}</div><div className="text-[10px] text-[#5E4A42]">{fmtPct(x.margine_pct)}</div></> : <span className="text-[#C9BDB3]">—</span>}
                </td>); })}
              <td className="px-3 py-2.5 text-right tabular font-semibold bg-[#FAF7F5]">{fmtEUR(m.margine)}<div className="text-[10px] font-normal text-[#756B64]">{fmtPct(m.margine_pct)}</div></td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-[#F4EFEA] font-semibold">
          <tr><td className="px-3 py-3">Totale tipologia</td>{linee.map((l) => <td key={l.linea} className="px-3 py-3 text-right tabular">{fmtEUR(l.margine)}<div className="text-[10px] font-normal text-[#756B64]">{fmtPct(l.margine_pct)}</div></td>)}<td className="px-3 py-3 text-right tabular" data-testid="matrice-totale">{fmtEUR(marche.reduce((s, m) => s + m.margine, 0))}</td></tr>
        </tfoot>
      </table>
    </div>
  );
}

export function SpeseBrandBreakdown({ sb, marche }) {
  const withSpese = marche.filter((m) => m.spese_brand > 0);
  return (
    <div className="space-y-5" data-testid="analisi-spese-brand">
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
            <tr>{["Marca", "Spese per categoria", "Spese brand", "Margine netto", "Margine netto marca", "ROI spese"].map((h, i) => <th key={h} className={`font-semibold px-3 py-3 ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#EAE3DC]">
            {withSpese.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#756B64]">Nessuna spesa brand attribuita a una marca nel periodo.</td></tr>}
            {withSpese.map((m, i) => (
              <tr key={m.marca_id || m.marca} data-testid={`spese-brand-marca-row-${i}`}>
                <td className="px-3 py-2.5 font-medium">{m.marca}</td>
                <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1.5">{m.spese_brand_per_categoria.map((c) => <span key={c.categoria} className="text-xs rounded-full px-2 py-0.5 bg-[#E8C5C8]/50 text-[#77594E]">{c.categoria} · {fmtEUR(c.valore)}</span>)}</div></td>
                <td className="px-3 py-2.5 text-right tabular text-[#A2513C]">{fmtEUR(m.spese_brand)}</td>
                <td className="px-3 py-2.5 text-right tabular">{fmtEUR(m.margine_netto)}</td>
                <td className={`px-3 py-2.5 text-right tabular font-semibold ${m.margine_netto_marca < 0 ? "text-[#B4553D]" : "text-[#4E6656]"}`}>{fmtEUR(m.margine_netto_marca)}</td>
                <td className="px-3 py-2.5 text-right tabular text-[#756B64]">{fmtPct(m.roi_spese_brand_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-soft p-5" data-testid="analisi-spese-generali">
        <div className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold mb-3">Spese generali (tutte le marche) · {fmtEUR(sb.totale_generale)}</div>
        <div className="flex flex-wrap gap-2">
          {sb.generale.length ? sb.generale.map((c) => <span key={c.categoria} className="text-sm rounded-full px-3 py-1 bg-[#F4EFEA]">{c.categoria} · <b className="tabular">{fmtEUR(c.valore)}</b></span>) : <span className="text-sm text-[#756B64]">Nessuna spesa generale nel periodo.</span>}
        </div>
        <p className="text-xs text-[#756B64] mt-3">{sb.nota}</p>
      </div>
    </div>
  );
}
